import type { RevenueSource } from './format';
import type { Settlement } from '../events/labels';

/** Finance (F29) as the API sends it. */
export const PROFIT_RESOURCE = 'finance.profitability';
export const FIXED_RESOURCE = 'finance.fixed_costs';
export const SUMMARIES_RESOURCE = 'finance.summaries';
export const SETTINGS_RESOURCE = 'finance.settings';
/** Owner 2026-11-04: „Koszty wydarzenia: dodawanie” — the event's manager adds its costs in „Rozliczenia”. */
export const EVENT_COSTS_RESOURCE = 'finance.event_costs';
export const FINANCE_KEY = ['finance'] as const;

export type CostResult = { costGrosze: number; resultGrosze: number; marginBp: number | null };
export type EventCostKind = 'dopozyczenie' | 'hotel' | 'catering' | 'paliwo' | 'inne';
export const COST_KINDS: Record<EventCostKind, string> = { dopozyczenie: 'Dopożyczenie', hotel: 'Hotel', catering: 'Catering', paliwo: 'Paliwo', inne: 'Inne' };
/** `createdByName` (owner 2026-11-04): who added it; `receiptFileId`: a photo of the receipt (absent from an older server). */
export type EventCost = { id: string; eventId: string; kind: EventCostKind; description: string; amountGrosze: number; version: number; createdBy?: string; createdByName?: string; receiptFileId?: string | null };
/** A cost in the manager's section „Koszty wydarzenia”: a field hidden by the rules (rule 7a) is missing. */
export type ManagerCost = Omit<EventCost, 'description' | 'amountGrosze'> & { description?: string; amountGrosze?: number };
export type CrewRow = {
  assignmentId: string;
  personName: string;
  functionName: string;
  status: 'proponowany' | 'potwierdzony' | 'odmowa';
  days: number;
  /** Only with the right to people's rates. */
  rateGrosze?: number | null;
  costGrosze?: number | null;
};
export type Profit = CostResult & {
  eventId: string;
  revenue: { netGrosze: number; source: RevenueSource; versionNumber: number | null; forecast: boolean };
  people: { planGrosze: number; actualGrosze: number | null; settledGrosze: number | null; missingRates: number };
  /** Services without a crew function (typed costs): the same in plan and actual. */
  servicesGrosze: number;
  equipmentGrosze: number;
  /** Typed equipment costs + the event's subrentals (F28); null without an actual column. */
  equipmentActualGrosze: number | null;
  /** The event's subrentals; null without the right to their costs. */
  subrentals: { totalGrosze: number; count: number } | null;
  transportGrosze: number;
  transportUnknown: boolean;
  manualGrosze: number;
  /** Owner 2026-11-04: the event's „Paliwo” — counted to the month, not in this result (absent from an older server). */
  fuelGrosze?: number;
  plan: CostResult;
  actual: CostResult | null;
  /** F45: with approved people's settlements; null without them or without the right to their amounts. */
  settled: CostResult | null;
  basis: 'rozliczenia' | 'obsada' | 'kosztorys';
  crew: CrewRow[];
  costs: EventCost[];
  distanceKm: number | null;
};

export type FixedPeriod = 'miesiecznie' | 'rocznie' | 'jednorazowo';
export const PERIODS: Record<FixedPeriod, string> = { miesiecznie: 'Co miesiąc', rocznie: 'Raz w roku', jednorazowo: 'Jednorazowo' };
/** Owner 2026-10-24: the payment status of a fixed cost's period. */
export type PaymentStatus = 'oplacone' | 'nieoplacone' | 'nieznany';
export type UnknownReason = 'brak-wskazowek' | 'brak-wyciagu' | 'przed-terminem';
/** The paying operation: its day always; other side and title with the statements' view; account and amount with their fields. */
export type PaidOperation = { id: string; bookingDate: string; counterpartyName?: string; title?: string; counterpartyIban?: string | null; amountGrosze?: number };
export type Occurrence = {
  month: string;
  dueDate: string;
  status: PaymentStatus;
  source: 'auto' | 'reczny';
  reason?: UnknownReason;
  note?: string;
  setAt?: string;
  auto: { status: PaymentStatus; reason?: UnknownReason };
  operation: PaidOperation | null;
};
export type FixedCost = {
  id: string;
  name: string;
  amountGrosze: number;
  period: FixedPeriod;
  fromMonth: string;
  toMonth: string | null;
  notes: string;
  /** Hints for matching the outgoing transfers ('' = not used); absent when hidden from the role. */
  matchRecipient?: string;
  matchAccount?: string;
  matchTitle?: string;
  /** The current period (the newest one up to today); null before the first. */
  current: Occurrence | null;
  version: number;
};

/** Owner 2026-11-04 (decision a): own transport of a period — actual fuel when the month has any, else the estimates. */
export type TransportLine = { basis: 'rzeczywiste' | 'szacunek' | 'mieszane'; transportGrosze: number; estimateGrosze: number; fuelGrosze: number };
export type Totals = {
  revenueConfirmedGrosze: number;
  revenueForecastGrosze: number;
  /** Without own transport (that is `transport`). */
  eventCostsGrosze: number;
  transport?: TransportLine;
  fixedCostsGrosze: number;
  /** Owner 2026-10-22 01:34: done company tasks' costs (in the result) and open ones (planned); only with the tasks' "Koszt". */
  taskCostsGrosze?: number;
  taskCostsPlannedGrosze?: number;
  resultGrosze: number;
  marginBp: number | null;
  events: number;
};
/** The month's company tasks with a cost; `tasks` only for whoever sees all tasks, `costCategory` only with "Rodzaj kosztu". */
export type TaskCosts = { doneGrosze: number; plannedGrosze: number; tasks?: Array<{ taskId: string; title: string; dueDay: string; done: boolean; costGrosze: number; costCategory?: string }> };
export type SummaryEvent = {
  eventId: string; name: string; startsAt: string; status: string; revenueGrosze: number; source: RevenueSource; forecast: boolean; costGrosze: number; resultGrosze: number; marginBp: number | null; basis: 'rozliczenia' | 'obsada' | 'kosztorys';
  /** Owner 2026-11-04: the event's „Rozliczenie” and version (to change it in the row) — only for whoever sees the field. */
  settlement?: Settlement;
  eventVersion?: number;
};
/** F45: the money of a period; each part only for a reader with its source's view and amounts (absent otherwise). */
export type Cash = {
  /** `withoutInvoiceGrosze` (owner 2026-10-22): payments of the forms without our invoice; only with the payments' amounts. */
  turnover?: Turnover;
  payments?: PaymentsReceived;
  missing?: { overdueGrosze: number; count: number };
  forecast?: { receivablesGrosze: number; installmentsGrosze: number; quotesGrosze: number; totalGrosze: number };
  investments?: { netGrosze: number; count: number };
  catalogPurchases?: { netGrosze: number };
};
export type PayMethod = 'przelew' | 'gotowka' | 'barter';
export const PAY_METHODS: Record<PayMethod, string> = { przelew: 'przelew', gotowka: 'gotówka', barter: 'barter' };
export type ExpectedPayment = {
  receivableId: string | null;
  eventId: string | null;
  eventName: string;
  contractorName: string;
  label: string;
  amountGrosze: number;
  dueDate: string | null;
  method: PayMethod;
  overdueDays: number;
  kind: 'naleznosc' | 'rata' | 'kosztorys';
};
export type MonthSummary = Cash & { month: string; events: SummaryEvent[]; totals: Totals; expected?: ExpectedPayment[]; taskCosts?: TaskCosts; /** Owner 2026-10-24: how many fixed costs' periods of the month are unpaid; only with the fixed costs' view. */ fixedUnpaid?: number };
export type YearSummary = { year: number; months: Array<Totals & Cash & { month: string }>; totals: Totals & Cash };
export type FuelKind = 'pb95' | 'on' | 'lpg';
/**
 * Own transport's cost (owner 2026-10-24: from fuel prices): per vehicle kind its fuel, consumption in ml per km
 * (= l/100 km × 10; null = not entered) and an extra per km; prices per litre typed by hand. `truckPerKmGrosze` /
 * `carPerKmGrosze`: the per km typed before, read only (used while no consumption is entered). A field hidden by the
 * field rules is missing (review I2).
 */
export type FinanceSettings = {
  truckPerKmGrosze?: number;
  carPerKmGrosze?: number;
  roundTrip?: boolean;
  truckFuel?: FuelKind;
  truckConsumptionMlPerKm?: number | null;
  truckExtraPerKmGrosze?: number;
  carFuel?: FuelKind;
  carConsumptionMlPerKm?: number | null;
  carExtraPerKmGrosze?: number;
  /** Owner 2026-10-24 10:22 / 16:15: „Stała kwota za wydarzenie” per vehicle kind (once per event). */
  truckPerDayGrosze?: number;
  carPerDayGrosze?: number;
  manualPb95Grosze?: number | null;
  manualOnGrosze?: number | null;
  manualLpgGrosze?: number | null;
  version: number;
};
export type FuelPrice = { fetched: { grosze: number; priceDate: string; source: string; sourceLabel: string; fetchedAt: string } | null; manualGrosze: number | null; effectiveGrosze: number | null };
/** Review I1: a typed price and a vehicle's per km are missing when the field rules hide them. */
export type FuelView = {
  prices: Record<FuelKind, Pick<FuelPrice, 'fetched'> & Partial<FuelPrice>>;
  lastAttempt: { at: string; ok: boolean; error: string; source: string } | null;
  perKm: Partial<Record<'truck' | 'car', { perKmGrosze: number; basis: string; mode: 'paliwo' | 'stawka' | 'brak-ceny' }>>;
  refreshed?: { ok: boolean; error?: string };
};

export type Turnover = { netGrosze: number; invoices: number; withoutInvoiceGrosze?: number };
/** By method and (`form…`, owner 2026-10-22) by the receivable's settlement form; form keys missing from an older server. */
export type PaymentsReceived = {
  przelewGrosze: number;
  gotowkaGrosze: number;
  barterGrosze: number;
  totalGrosze: number;
  formFakturaGrosze?: number;
  formPrzelewGrosze?: number;
  formUmowaGrosze?: number;
  formGotowkaGrosze?: number;
  formFakturaInnaGrosze?: number;
  formBarterGrosze?: number;
};
